import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Lock, UserPlus, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RegisterPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [telegram, setTelegram] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Заполните обязательные поля");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Пароли не совпадают");
      return;
    }

    if (password.length < 6) {
      toast.error("Пароль должен быть минимум 6 символов");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/user/register`, {
        email,
        password,
        telegram: telegram || null
      });

      localStorage.setItem("user_token", response.data.token);
      localStorage.setItem("user_data", JSON.stringify(response.data.user));
      toast.success("Регистрация успешна!");
      navigate("/cabinet");
    } catch (error) {
      console.error("Register error:", error);
      toast.error(error.response?.data?.detail || "Ошибка регистрации");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-slate-300 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            size="icon"
            className="hover:bg-blue-50 hover:text-blue-600"
          >
            <ArrowLeft size={24} />
          </Button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <span className="text-white font-black text-xl">S</span>
            </div>
            <span className="font-bold text-xl text-slate-800 tracking-tight">
              SKIN<span className="text-blue-600">KOSH</span>
            </span>
          </Link>
        </div>
      </header>

      {/* Register Form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full bg-white border border-slate-300 p-8 rounded-2xl shadow-xl">
          <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 flex items-center justify-center rounded-xl">
            <UserPlus className="text-blue-600" size={32} />
          </div>
          
          <h2 className="text-2xl font-bold mb-2 text-center text-slate-800">
            Регистрация
          </h2>
          <p className="text-slate-500 text-center mb-8">
            Создайте аккаунт для отслеживания обменов
          </p>

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-600 mb-2 block font-medium">
                <Mail size={16} className="inline mr-2" />
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="h-12 bg-slate-50 border-slate-300 focus:border-blue-500"
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-600 mb-2 block font-medium">
                <Lock size={16} className="inline mr-2" />
                Пароль *
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Минимум 6 символов"
                required
                className="h-12 bg-slate-50 border-slate-300 focus:border-blue-500"
                data-testid="register-password"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-600 mb-2 block font-medium">
                <Lock size={16} className="inline mr-2" />
                Подтвердите пароль *
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Повторите пароль"
                required
                className="h-12 bg-slate-50 border-slate-300 focus:border-blue-500"
                data-testid="register-confirm-password"
              />
            </div>

            <div>
              <Label htmlFor="telegram" className="text-slate-600 mb-2 block font-medium">
                <MessageCircle size={16} className="inline mr-2" />
                Telegram (необязательно)
              </Label>
              <Input
                id="telegram"
                type="text"
                value={telegram}
                onChange={(e) => setTelegram(e.target.value)}
                placeholder="@username"
                className="h-12 bg-slate-50 border-slate-300 focus:border-blue-500"
                data-testid="register-telegram"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold text-lg shadow-lg shadow-blue-500/30 mt-2"
              data-testid="register-submit"
            >
              {loading ? "Загрузка..." : "Зарегистрироваться"}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-slate-500">
              Уже есть аккаунт?{" "}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold">
                Войти
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
